import { ChevronRight } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router";

import { useLogout, useSwitchOrganization } from "@/auth/session";
import { useSession } from "@/auth/session-context";
import { useDocumentTitle } from "@/components/page";
import { paths } from "@/routes/paths";
import { Button } from "@punto-firma/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@punto-firma/ui/components/card";
import { FullScreenLoader } from "@punto-firma/ui/components/full-screen-loader";

import { CenteredScreen } from "./centered-screen";

/**
 * Tras el login, si el email está en varias organizaciones (una gestoría) y la
 * sesión aún no tiene una, se elige aquí: cada token es de una sola
 * organización. Con una sola se entra sin preguntar; sin ninguna, a «Sin
 * acceso» (o a Plataforma si es administrador de plataforma).
 */
export const SelectOrganizationPage = () => {
  const { t } = useTranslation(["auth", "common"]);
  useDocumentTitle(t("auth:selectOrganization.title"));
  const { me, currentOrganization, isPlatformAdmin } = useSession();
  const switchOrganization = useSwitchOrganization();
  const logout = useLogout();
  const { organizations } = me;
  const onlyOrganization =
    organizations.length === 1 ? organizations[0] : undefined;
  const { mutate, isIdle } = switchOrganization;

  useEffect(() => {
    if (!currentOrganization && onlyOrganization && isIdle) {
      mutate(onlyOrganization.id);
    }
  }, [currentOrganization, onlyOrganization, isIdle, mutate]);

  if (currentOrganization) return <Navigate to={paths.home} replace />;
  if (organizations.length === 0) {
    return isPlatformAdmin ? (
      <Navigate to={paths.platformOrganizations} replace />
    ) : (
      <Navigate to={`${paths.noAccess}?reason=no_organizations`} replace />
    );
  }
  if (onlyOrganization && !switchOrganization.isError)
    return <FullScreenLoader />;

  return (
    <CenteredScreen>
      <Card>
        <CardHeader>
          <CardTitle>{t("auth:selectOrganization.title")}</CardTitle>
          <CardDescription>
            {t("auth:selectOrganization.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ul className="divide-y rounded-md border">
            {organizations.map((organization) => (
              <li key={organization.id}>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
                  disabled={switchOrganization.isPending}
                  onClick={() => switchOrganization.mutate(organization.id)}
                >
                  <span className="flex-1 font-medium">
                    {organization.name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {t(`common:roles.${organization.role}`)}
                  </span>
                  <ChevronRight
                    className="size-4 text-muted-foreground"
                    aria-hidden
                  />
                </button>
              </li>
            ))}
          </ul>
          <p className="text-sm text-muted-foreground">
            {me.user.email} ·{" "}
            <Button
              variant="link"
              className="h-auto p-0 text-muted-foreground"
              onClick={() => logout.mutate()}
            >
              {t("auth:selectOrganization.otherAccount")}
            </Button>
          </p>
          {isPlatformAdmin ? (
            <Button asChild variant="outline" size="sm">
              <Link to={paths.platformOrganizations}>
                {t("auth:selectOrganization.goToPlatform")}
              </Link>
            </Button>
          ) : null}
        </CardContent>
      </Card>
    </CenteredScreen>
  );
};
